/**
 * NotificationBell — иконка-колокольчик в шапке App с DS-popover-меню.
 * Wave 2 / NOTIFICATIONS — UI_FIX заход 1: AntD → DS.
 *
 * - Без AntD `Dropdown/Spin/Badge/Button/Tag/message`.
 * - DS popover (click-outside + Escape + Tab focus) с CSS-классами `.mb-notification-*`.
 * - `toastAPI` + `pickSafeRussianBackendMessage` (без EN-протечек, без console).
 * - Iconography — Lucide; `var(--brand)` / `var(--err)` / `var(--text-2)` без HEX.
 * - Counter polling 30 сек + visibilitychange — без двойного toast при flap.
 */

import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCircle2,
  Trash2,
  AlertCircle,
  AlertTriangle,
  Info,
  CreditCard,
  Package,
  Truck,
  Users,
  ShieldAlert,
  Blocks,
  Check,
  X,
} from 'lucide-react';
import notificationService from '../../services/notificationService';
import {
  pickSafeRussianBackendMessage,
  pickHttpStatusRussianFallback,
} from '../../utils/safeRussianBackendMessage';
import { devLogApiError } from '../../utils/devLogApiError';
import { toastAPI } from '../../contexts/ToastContext';
import './NotificationBell.css';

export const NOTIFICATIONS_UNREAD_EVENT = 'mb_notifications_unread';

const POLL_INTERVAL_MS = 30000;
const PREVIEW_LIMIT = 10;
const MENU_Z = 1300;

const PRIORITY_BADGE_CLASS = {
  critical: 'mb-badge mb-badge--err',
  high: 'mb-badge mb-badge--warn',
  normal: 'mb-badge mb-badge--info',
  low: 'mb-badge mb-badge--gray',
};

const PRIORITY_LABELS = {
  critical: 'Критично',
  high: 'Важно',
};

function getCategoryIcon(item) {
  const categoryMap = {
    billing: { Cmp: CreditCard, color: 'var(--warn)' },
    orders: { Cmp: Package, color: 'var(--brand)' },
    deliveries: { Cmp: Truck, color: 'var(--info)' },
    inventory: { Cmp: Info, color: 'var(--text-2)' },
    hr: { Cmp: Users, color: 'var(--ok)' },
    security: { Cmp: ShieldAlert, color: 'var(--err)' },
    system: { Cmp: Blocks, color: 'var(--ok)' },
    integrations: { Cmp: Blocks, color: 'var(--warn)' },
  };
  const typeMap = {
    error: { Cmp: AlertCircle, color: 'var(--err)' },
    warning: { Cmp: AlertTriangle, color: 'var(--warn)' },
    success: { Cmp: CheckCircle2, color: 'var(--ok)' },
    process_completed: { Cmp: CheckCircle2, color: 'var(--ok)' },
    process_failed: { Cmp: AlertCircle, color: 'var(--err)' },
  };
  const slot =
    categoryMap[item.category] ||
    typeMap[item.type] ||
    typeMap[item.event_type?.split('.')[0]] ||
    { Cmp: Info, color: 'var(--brand)' };
  const { Cmp, color } = slot;
  return <Cmp size={18} color={color} aria-hidden="true" />;
}

function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const diff = Math.floor((Date.now() - date) / 1000);
  if (Number.isNaN(diff)) return '';
  if (diff < 60) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  return date.toLocaleDateString('ru-RU');
}

const NotificationBell = ({ variant = 'header', showLabel = false }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [listError, setListError] = useState(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);
  const pollOkRef = useRef(true);

  const publishUnread = useCallback((count) => {
    const n = typeof count === 'number' ? count : 0;
    try {
      window.dispatchEvent(
        new CustomEvent(NOTIFICATIONS_UNREAD_EVENT, { detail: { count: n } })
      );
    } catch {
      /* ignore */
    }
  }, []);

  const loadUnreadCount = useCallback(async () => {
    try {
      const data = await notificationService.getUnreadCount();
      pollOkRef.current = true;
      const next = data.count ?? 0;
      setUnreadCount(next);
      publishUnread(next);
    } catch (err) {
      setUnreadCount(0);
      publishUnread(0);
      devLogApiError('NotificationBell: счётчик непрочитанных', err);
      /* axios-interceptor уже показывает ошибки запросов; второй toast не нужен */
    }
  }, [publishUnread]);

  const loadNotifications = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setListError(null);
    try {
      const data = await notificationService.getNotifications({ limit: PREVIEW_LIMIT });
      const items = data.items || [];
      setNotifications(items);
      if (typeof data.unread === 'number') {
        setUnreadCount(data.unread);
        publishUnread(data.unread);
      } else {
        const next = items.filter((n) => !n.is_read).length;
        setUnreadCount(next);
        publishUnread(next);
      }
    } catch (err) {
      devLogApiError('NotificationBell: превью списка', err);
      setNotifications([]);
      setListError(
        pickSafeRussianBackendMessage(
          err?.response?.data?.error || err?.response?.data?.message,
          pickHttpStatusRussianFallback(err?.response?.status) ||
            'Не удалось загрузить уведомления.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadUnreadCount();
    }, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadUnreadCount]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = Math.min(380, window.innerWidth - 24);
      let left = rect.right - width;
      left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
      const top = Math.min(rect.bottom + 6, window.innerHeight - 24);
      setMenuStyle({
        position: 'fixed',
        top,
        left,
        width,
        zIndex: MENU_Z,
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const togglePopover = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        const el = wrapperRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const width = Math.min(380, window.innerWidth - 24);
          let left = rect.right - width;
          left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
          const top = Math.min(rect.bottom + 6, window.innerHeight - 24);
          setMenuStyle({
            position: 'fixed',
            top,
            left,
            width,
            zIndex: MENU_Z,
          });
        }
        loadNotifications();
      }
      return next;
    });
  };

  const markAsRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
      setUnreadCount((prev) => {
        const next = Math.max(0, prev - 1);
        publishUnread(next);
        return next;
      });
    } catch (err) {
      devLogApiError('NotificationBell: отметить прочитанным', err);
      toastAPI?.error?.(
        pickSafeRussianBackendMessage(
          err?.response?.data?.error || err?.response?.data?.message,
          pickHttpStatusRussianFallback(err?.response?.status) ||
            'Не удалось отметить уведомление прочитанным.',
        ),
      );
    }
  };

  const deleteNotification = async (id, e) => {
    if (e) e.stopPropagation();
    const item = notifications.find((n) => n.id === id);
    try {
      await notificationService.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (item && !item.is_read) {
        setUnreadCount((prev) => {
          const next = Math.max(0, prev - 1);
          publishUnread(next);
          return next;
        });
      }
    } catch (err) {
      devLogApiError('NotificationBell: удаление', err);
      toastAPI?.error?.(
        pickSafeRussianBackendMessage(
          err?.response?.data?.error || err?.response?.data?.message,
          pickHttpStatusRussianFallback(err?.response?.status) ||
            'Не удалось удалить уведомление.',
        ),
      );
    }
  };

  const markAllAsRead = async (e) => {
    if (e) e.stopPropagation();
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      publishUnread(0);
    } catch (err) {
      devLogApiError('NotificationBell: прочитать все', err);
      toastAPI?.error?.(
        pickSafeRussianBackendMessage(
          err?.response?.data?.error || err?.response?.data?.message,
          pickHttpStatusRussianFallback(err?.response?.status) ||
            'Не удалось отметить все уведомления прочитанными.',
        ),
      );
    }
  };

  const handleNotificationClick = async (item) => {
    if (!item.is_read) await markAsRead(item.id);
    if (item.action_url) {
      const url = item.action_url;
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    }
    setOpen(false);
  };

  const goAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  return (
    <div
      ref={wrapperRef}
      className={
        variant === 'sidebar'
          ? 'mb-notification-bell-wrap mb-notification-bell-wrap--sidebar'
          : 'mb-notification-bell-wrap'
      }
      style={{ position: 'relative', display: variant === 'sidebar' ? 'block' : 'inline-flex', width: variant === 'sidebar' ? '100%' : undefined }}
    >
      {variant === 'sidebar' ? (
        <button
          type="button"
          className="mb-helper-sidebar__item"
          onClick={togglePopover}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={
            unreadCount > 0
              ? `Уведомления, непрочитанных: ${unreadCount}`
              : 'Уведомления'
          }
        >
          <span className="mb-helper-sidebar__item-icon mb-helper-sidebar__item-icon--brand">
            <Bell size={18} aria-hidden="true" />
            {unreadCount > 0 && (
              <span className="mb-helper-sidebar__item-count">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </span>
          {showLabel && (
            <span className="mb-helper-sidebar__item-label">Уведомления</span>
          )}
        </button>
      ) : (
        <button
          type="button"
          className="mb-btn mb-btn--ghost mb-btn--icon mb-notification-bell-btn"
          onClick={togglePopover}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={
            unreadCount > 0
              ? `Уведомления, непрочитанных: ${unreadCount}`
              : 'Уведомления'
          }
          style={{ position: 'relative' }}
        >
          <Bell
            size={18}
            color={unreadCount > 0 ? 'var(--brand)' : 'var(--text-2)'}
            aria-hidden="true"
          />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: 8,
                background: 'var(--err)',
                color: 'var(--text-on-brand, #fff)',
                fontSize: 10,
                fontWeight: 600,
                lineHeight: '16px',
                textAlign: 'center',
                boxShadow: '0 0 0 2px var(--bg-surface)',
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-label="Уведомления"
            className="mb-notification-menu"
            style={menuStyle || { position: 'fixed', zIndex: MENU_Z }}
          >
          <div className="mb-notification-header">
            <span className="mb-notification-title">Центр уведомлений</span>
            <div style={{ display: 'inline-flex', gap: 'var(--s1)' }}>
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="mb-btn mb-btn--ghost mb-btn--sm"
                  onClick={markAllAsRead}
                  aria-label="Отметить все прочитанными"
                >
                  <Check size={14} aria-hidden="true" />
                  <span>Прочитать все</span>
                </button>
              )}
              <button
                type="button"
                className="mb-btn mb-btn--ghost mb-btn--sm mb-btn--icon"
                onClick={() => setOpen(false)}
                aria-label="Закрыть"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="mb-notification-body">
            {loading ? (
              <div className="mb-notification-loading" aria-live="polite">
                <span className="mb-spin mb-spin--sm" aria-hidden="true" />
                <span style={{ marginLeft: 'var(--s2)', color: 'var(--text-2)' }}>
                  Загрузка…
                </span>
              </div>
            ) : listError ? (
              <div className="mb-notification-error" role="alert">
                <div className="mb-notification-error-text">{listError}</div>
                <button
                  type="button"
                  className="mb-btn mb-btn--link mb-btn--sm"
                  onClick={() => loadNotifications()}
                >
                  Повторить
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="mb-notification-empty">Нет новых уведомлений</div>
            ) : (
              notifications.map((item) => (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  className={`mb-notification-item ${
                    !item.is_read ? 'is-unread' : ''
                  }`}
                  onClick={() => handleNotificationClick(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNotificationClick(item);
                    }
                  }}
                >
                  <div className="mb-notification-icon">{getCategoryIcon(item)}</div>
                  <div className="mb-notification-content">
                    <div className="mb-notification-item-title">
                      <span>{item.title}</span>
                      {item.priority &&
                        item.priority !== 'normal' &&
                        PRIORITY_LABELS[item.priority] && (
                          <span
                            className={`${
                              PRIORITY_BADGE_CLASS[item.priority] ||
                              'mb-badge mb-badge--gray'
                            } mb-badge--sm mb-notification-priority-tag`}
                          >
                            {PRIORITY_LABELS[item.priority]}
                          </span>
                        )}
                    </div>
                    {item.message && (
                      <div className="mb-notification-item-message">{item.message}</div>
                    )}
                    <div className="mb-notification-item-time">
                      {formatTime(item.created_at)}
                    </div>
                  </div>
                  <div className="mb-notification-actions">
                    {!item.is_read && (
                      <button
                        type="button"
                        className="mb-btn mb-btn--ghost mb-btn--sm mb-btn--icon"
                        onClick={(e) => markAsRead(item.id, e)}
                        aria-label="Отметить как прочитанное"
                        title="Прочитано"
                      >
                        <CheckCircle2
                          size={14}
                          color="var(--brand)"
                          aria-hidden="true"
                        />
                      </button>
                    )}
                    <button
                      type="button"
                      className="mb-btn mb-btn--ghost mb-btn--sm mb-btn--icon"
                      onClick={(e) => deleteNotification(item.id, e)}
                      aria-label="Удалить уведомление"
                      title="Удалить"
                    >
                      <Trash2 size={14} color="var(--err)" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <button
            type="button"
            className="mb-notification-footer"
            onClick={goAll}
            aria-label="Открыть все уведомления"
          >
            Открыть все уведомления
          </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default NotificationBell;
